/**
 * "YOUR CHART" - BIRTH DATA AND THE STAR SIGNS WORKED OUT FROM IT - `setBirthData`,
 * `clearBirthData`, `setComputedSignsVisibility` (LOCKED D32; register R-016), and the place search
 * behind the birth place, `searchBirthPlaces` (wave 26; ../data/usePlaceSearch.ts).
 *
 * ★ THE LAST CARD OF THE AUTHOR PROFILE PAGE (design 24, T19), where the prototype draws it; it was
 * Settings' until then. Still saved by its own button, never by a section's "Save this section", and
 * drawn only where Settings drew it - for an author with a cabinet (the page beside the sidebar,
 * ../screens/ApplicationScreen.tsx). Its writes are the cabinet client's, as they were.
 *
 * ★ WRITE-ONLY, SO THE FORM IS NEVER PRE-FILLED. The date, time and zone are encrypted at rest and
 * "appear in no response on any surface" (authors-api.yaml, `ComputedSignsState`). What can be read
 * back is whether a date is on file and the three sign codes. So the section says "on file" or "not
 * on file", shows the signs, and asks for the whole birth data again to change it - an empty form
 * never means "cleared". Removing is its own act, `clearBirthData`, behind its own confirmation.
 *
 * ★★ THE PLACE IS PICKED, NEVER TYPED. A rising sign needs `birthPlaceId`, "a stable id from the
 * platform's own geocoding service", which the platform turns into coordinates itself; a typed name
 * is a label and "never used for computation". So the place field searches as the author types
 * (`usePlaceSearch`, after a short pause and from two characters) and only a place PICKED from
 * the list is sent - as `birthPlace` (its label) with `birthPlaceId`. Text that was not picked is
 * refused on this screen with a sentence, not sent as a label that can never give a sign.
 *
 * ★ THE TIME ZONE IS THE PICKED PLACE'S, WITH NO FIELD OF ITS OWN (the owner, 2026-10-19). The place
 * search answers each settlement with its IANA zone, and that zone is sent as `birthTimeZone`. It is
 * the place's zone TODAY, which the IANA id's own history turns into the right offset for the birth
 * date; only a birth in a settlement that has since moved between zone ids would be read in the
 * wrong one, and the author had to know that to fix it before, too.
 *
 * A PLACE SEARCH THAT FAILS DOES NOT STOP THE SAVE. It says so, and the birth data can still be
 * saved without a place and without a time - the sun sign needs the date alone.
 *
 * THE TIME AND THE ZONE TRAVEL TOGETHER: "Sent with `birthTime` or neither; sending one alone is
 * refused by `birth_time_needs_zone`". The zone comes from the place, so a time needs a picked place
 * (`validate` says so on screen), and a form with no time sends neither.
 *
 * THE PUBLIC SWITCH is the consent to show the signs on the public author page. It cannot be on with
 * nothing to show (`BIRTH_DATE_REQUIRED`). With a date on file it acts at once. ★ With none on file
 * it can still be turned on as soon as the form holds a date (design 24, T19: a teammate typed a
 * date and the switch "would not turn on") - and then nothing is sent until "Save birth data", which
 * carries the choice in the same write (`BirthDataWrite.computedSignsPublic`), so the date and the
 * consent cannot land apart. Removing the birth data turns it off - the platform does both.
 *
 * DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): a section card (the kit's
 * SectionCard) with the "on file" line in an inset; since T19 with no small word at its right - the
 * switch says whether the signs are public. "The platform" is "MoonX" in what an author reads.
 */
import {
  Autocomplete,
  Button,
  Group,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { Sparkles } from '@moonx/ui';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from '../../../i18n';
import { useCabinetApi } from '../../cabinet/api';
import type { OperationBody } from '../../cabinet/api';
import { ErrorState, SectionCard, SectionInset } from '../../cabinet/components';
import { useAction } from '../../cabinet/state';
import type { ComputedSignsState } from '../contract';
import { PLACE_SEARCH_MAX, usePlaceSearch } from '../data/usePlaceSearch';
import type { PlaceSuggestion } from '../data/usePlaceSearch';

type SignsState = ComputedSignsState;
type BirthDataWrite = OperationBody<'setBirthData'>;
type BirthPlace = PlaceSuggestion;

interface FieldError {
  readonly field: 'date' | 'time' | 'place';
  /** The sentence's `application` catalogue key, worded where the field shows it. */
  readonly message: `chart.error.${'date' | 'time' | 'place' | 'timeNeedsPlace'}`;
}

/** What the platform holds once the birth data is removed - the answer `clearBirthData` implies. */
const CLEARED: SignsState = {
  birthDataOnFile: false,
  computedSignsPublic: false,
  sunSign: null,
  moonSign: null,
  risingSign: null,
  zodiacSystem: null,
};

/** The twelve signs' names, keyed on the zodiac lookup's own codes (`zodiac_sign.code`). */
const SIGN_NAME = {
  aries: 'chart.sign.aries',
  taurus: 'chart.sign.taurus',
  gemini: 'chart.sign.gemini',
  cancer: 'chart.sign.cancer',
  leo: 'chart.sign.leo',
  virgo: 'chart.sign.virgo',
  libra: 'chart.sign.libra',
  scorpio: 'chart.sign.scorpio',
  sagittarius: 'chart.sign.sagittarius',
  capricorn: 'chart.sign.capricorn',
  aquarius: 'chart.sign.aquarius',
  pisces: 'chart.sign.pisces',
} as const;

/** `aries` -> `Aries`; a code the catalogue does not know is spelled from the code itself. */
function signWords(code: string | null | undefined, t: TFunction<'application'>): string | null {
  if (code === undefined || code === null || code === '') {
    return null;
  }
  return Object.hasOwn(SIGN_NAME, code)
    ? t(SIGN_NAME[code as keyof typeof SIGN_NAME])
    : `${code.charAt(0).toUpperCase()}${code.slice(1)}`;
}

function validate(
  date: string,
  time: string,
  place: { readonly text: string; readonly picked: BirthPlace | null },
): FieldError | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return { field: 'date', message: 'chart.error.date' };
  }
  if (time !== '' && !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
    return { field: 'time', message: 'chart.error.time' };
  }
  if (place.text.trim() !== '' && place.picked === null) {
    return { field: 'place', message: 'chart.error.place' };
  }
  // The time zone is the picked place's (the owner, 2026-10-19), so a time needs a place.
  if (time !== '' && place.picked === null) {
    return { field: 'place', message: 'chart.error.timeNeedsPlace' };
  }
  return null;
}

export function BirthDataSection({ initial }: { readonly initial: SignsState }) {
  const { t } = useTranslation('application');
  const api = useCabinetApi();

  const [signs, setSigns] = useState<SignsState>(initial);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [placeText, setPlaceText] = useState('');
  const [picked, setPicked] = useState<BirthPlace | null>(null);
  const [fieldError, setFieldError] = useState<FieldError | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  // The switch's choice made before any date is on file - sent with the save, never on its own.
  const [publishOnSave, setPublishOnSave] = useState(false);
  const search = usePlaceSearch(placeText, picked);

  const save = useAction((write: BirthDataWrite) => api.setBirthData(write));
  // `true` once erased: `clearBirthData` answers 204 with no body, and `run` gives `undefined` for a
  // failure, so the two outcomes need a value to tell them apart.
  const remove = useAction(async () => {
    await api.clearBirthData();
    return true as const;
  });
  const publish = useAction((enabled: boolean) => api.setComputedSignsVisibility({ enabled }));
  const busy = save.isRunning || remove.isRunning || publish.isRunning;
  /** No date on file, and one typed: the switch records a choice for the save to carry. */
  const choosingBeforeSave = !signs.birthDataOnFile && date !== '';
  const signsSwitchOn = signs.birthDataOnFile
    ? signs.computedSignsPublic
    : choosingBeforeSave && publishOnSave;

  const onSave = () => {
    const problem = validate(date, time, { text: placeText, picked });
    setFieldError(problem);
    if (problem !== null) {
      return;
    }
    // A time travels with its zone, and the zone is the picked place's (`validate` holds the pair).
    const zone = time !== '' && picked !== null ? picked.birthTimeZone : null;
    void save
      .run({
        birthDate: date,
        birthTime: zone === null ? null : time,
        birthTimeZone: zone,
        // Only a PICKED place is sent, and always as the pair `birth_place_needs_id` wants.
        ...(picked === null
          ? {}
          : { birthPlace: picked.birthPlace, birthPlaceId: picked.birthPlaceId }),
        // The switch turned on before a date was on file rides on the same write, so the date and
        // the consent cannot land apart. Left off, nothing is said about publication at all.
        ...(signsSwitchOn && !signs.birthDataOnFile ? { computedSignsPublic: true } : {}),
      })
      .then((recorded) => {
        if (recorded !== undefined) {
          setSigns(recorded);
          setPublishOnSave(false);
          // Write-only: once recorded, the values leave the page rather than sit in a form.
          setDate('');
          setTime('');
          setPlaceText('');
          setPicked(null);
        }
      });
  };

  const onRemove = () => {
    void remove.run().then((erased) => {
      setConfirmingRemoval(false);
      if (erased === true) {
        setSigns(CLEARED);
      }
    });
  };

  const sun = signWords(signs.sunSign, t);
  const moon = signWords(signs.moonSign, t);
  const rising = signWords(signs.risingSign, t);
  const errorFor = (field: FieldError['field']): string | undefined =>
    fieldError?.field === field ? t(fieldError.message) : undefined;

  return (
    <SectionCard title={t('chart.title')} icon={Sparkles}>
      <Stack gap="md">
        <SectionInset>
          {signs.birthDataOnFile ? (
            <Stack gap={4}>
              <Text size="sm" fw={700}>
                {t('chart.onFile')}
              </Text>
              <Text size="sm">
                {/* The platform returns the signs only while the switch is on, to the author as well
                    (authors-api.yaml `ComputedSignsState`), so a date on file with no sign is private. */}
                {sun === null && !signs.computedSignsPublic
                  ? t('chart.signsPrivate')
                  : t('chart.signs', {
                      sun: sun ?? t('chart.sunMissing'),
                      moon: moon ?? t('chart.moonMissing'),
                      rising: rising ?? t('chart.risingMissing'),
                    })}
              </Text>
            </Stack>
          ) : (
            <Text size="sm" fw={700}>
              {t('chart.notOnFile')}
            </Text>
          )}
        </SectionInset>
        <Text size="xs" c="dimmed">
          {t('chart.neverShown')}
        </Text>

        <Switch
          // The page's one wording for a public switch (`visibilityWords`); its name says what.
          label={t('fields.visibility.on')}
          aria-label={t('chart.switch.label')}
          checked={signsSwitchOn}
          disabled={busy || !(signs.birthDataOnFile || choosingBeforeSave)}
          description={
            choosingBeforeSave && signsSwitchOn
              ? t('chart.switch.onceSaved')
              : signs.birthDataOnFile || choosingBeforeSave
                ? t('chart.switch.saysALot')
                : t('chart.switch.needsDate')
          }
          onChange={(event) => {
            const enabled = event.currentTarget.checked;
            if (!signs.birthDataOnFile) {
              setPublishOnSave(enabled);
              return;
            }
            void publish.run(enabled).then((recorded) => {
              if (recorded !== undefined) {
                setSigns(recorded);
              }
            });
          }}
        />
        {publish.error === undefined ? null : (
          <ErrorState title={t('chart.switch.notChanged')} error={publish.error} />
        )}

        <Stack gap="sm">
          <Text size="sm" fw={700} component="h3">
            {signs.birthDataOnFile ? t('chart.form.replaceTitle') : t('chart.form.addTitle')}
          </Text>
          {/* Two equal columns, each a label, a line and a box, so the boxes sit level (the owner,
              2026-10-19: they did not, the date having no line and a different width). */}
          <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
            <TextInput
              type="date"
              label={t('chart.form.date')}
              description={t('chart.form.dateDescription')}
              value={date}
              onChange={(event) => {
                setDate(event.currentTarget.value);
                setFieldError(null);
              }}
              error={errorFor('date')}
              disabled={busy}
              required
            />
            <TextInput
              type="time"
              label={t('chart.form.time')}
              description={t('chart.form.timeDescription')}
              value={time}
              onChange={(event) => {
                setTime(event.currentTarget.value);
                setFieldError(null);
              }}
              error={errorFor('time')}
              disabled={busy}
            />
          </SimpleGrid>
          <Stack gap={4}>
            <Autocomplete
              label={t('chart.form.place')}
              description={t('chart.form.placeDescription')}
              // The suggestions are the platform's, already matched - this field filters nothing.
              data={search.places.map((place) => ({
                value: place.birthPlaceId,
                label: place.birthPlace,
              }))}
              filter={({ options }) => options}
              value={placeText}
              maxLength={PLACE_SEARCH_MAX}
              onChange={(value) => {
                setPlaceText(value);
                // Editing a picked place un-picks it; picking one sets the text to its own label.
                setPicked((current) =>
                  current !== null && current.birthPlace !== value ? null : current,
                );
                setFieldError(null);
              }}
              onOptionSubmit={(birthPlaceId) => {
                const place = search.places.find(
                  (candidate) => candidate.birthPlaceId === birthPlaceId,
                );
                if (place !== undefined) {
                  // The pick carries its zone, which the save sends with a time of birth.
                  setPicked(place);
                }
              }}
              error={errorFor('place')}
              disabled={busy}
            />
            {search.status === 'failed' ? (
              <Text size="xs" c="dimmed" role="status">
                {t('chart.form.searchFailed')}
              </Text>
            ) : null}
            {search.status === 'ready' && search.places.length === 0 && picked === null ? (
              <Text size="xs" c="dimmed" role="status">
                {t('location.noMatch')}
              </Text>
            ) : null}
          </Stack>
          {save.error === undefined ? null : (
            <ErrorState title={t('chart.form.notSaved')} error={save.error} />
          )}
          <Group>
            <Button onClick={onSave} loading={save.isRunning} disabled={busy}>
              {signs.birthDataOnFile ? t('chart.form.replace') : t('chart.form.save')}
            </Button>
          </Group>
        </Stack>

        {signs.birthDataOnFile ? (
          confirmingRemoval ? (
            <Stack gap="xs" role="group" aria-label={t('chart.remove.groupLabel')}>
              <Text size="sm">{t('chart.remove.body')}</Text>
              <Group gap="sm">
                <Button variant="filled" onClick={onRemove} loading={remove.isRunning}>
                  {t('chart.remove.confirm')}
                </Button>
                <Button
                  variant="default"
                  data-autofocus
                  onClick={() => {
                    setConfirmingRemoval(false);
                  }}
                  disabled={remove.isRunning}
                >
                  {t('chart.remove.keep')}
                </Button>
              </Group>
            </Stack>
          ) : (
            <Group>
              <Button
                variant="subtle"
                onClick={() => {
                  setConfirmingRemoval(true);
                }}
                disabled={busy}
              >
                {t('chart.remove.start')}
              </Button>
            </Group>
          )
        ) : null}
        {remove.error === undefined ? null : (
          <ErrorState title={t('chart.remove.notRemoved')} error={remove.error} />
        )}
      </Stack>
    </SectionCard>
  );
}
